import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { NotificationSettings } from 'src/app/shared/constants/notifications';
import { AccountService } from 'src/app/shared/services/account.service';
import { CacheService } from 'src/app/shared/services/cache.service';
import { environment } from 'src/environments/environment';

import lodash from 'lodash';
import urlJoin from 'url-join';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private baseClientUrl = environment.API_URL;

  constructor(
    private http: HttpClient,
    private accountService: AccountService,
    private cache: CacheService,
  ) {}

  public getUserList(accountId: number, params = {}, skipCache = false) {
    const url = urlJoin(this.baseClientUrl, 'user', '/');

    params = {
      account: accountId,
      ...params,
    };

    return this.http.get(url, {
      params,
      headers: this.cache.buildCacheSkipHeader(skipCache),
    });
  }

  public addUser(body) {
    const url = urlJoin(this.baseClientUrl, 'user', '/');
    return this.http.post(url, body);
  }

  public updateUser(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `user/${id}/`);
    return this.http.patch(url, body);
  }

  public getUser(id: number) {
    const url = urlJoin(this.baseClientUrl, `user/${id}/`);
    return this.http.get(url);
  }

  public async getUserListMultipleAccounts(accounts: number[], params = {}) {
    return lodash.flatten(
      await Promise.all(accounts.map((account) => {
        return this.getUserList(account, params)
          .toPromise();
      })),
    );
  }

  getFullUserName(user: any) {
    return `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
  }

  public async getAllPlatformAdminUsers() {
    // Get admin accounts.
    const admins = await this.accountService
      .getAdminAccountList() as any[];

    // Get users against all accounts.
    const users = await this.getUserListMultipleAccounts(
      admins.map((account) => account.id),
    );

    return users.map((user: any) => {
      return {
        ...user,
        name: this.getFullUserName(user),
      };
    });
  }

  public async getUserNotificationSettings(userId: any = null): Promise<NotificationSettings> {
    let url = urlJoin(this.baseClientUrl, '/notification-settings');

    if (userId) {
      url = urlJoin(url, `?user=${userId}`);
    }

    return await this.http.get(url).toPromise() as NotificationSettings;
  }

  public async updateUserNotificationSettings(notificationSettings: NotificationSettings, userId: any = null) {
    let url = urlJoin(this.baseClientUrl, '/notification-settings');

    if (userId) {
      url = urlJoin(url, `?user=${userId}`);
    }

    return this.http.put(url, notificationSettings).toPromise();
  }
}
